import {IServiceFactoryExtended} from '../../../../service-factory-extended.interface';
import {VideoLiveStreamBasePlayer} from '../video-live-stream-player-base';
import Hls, {Events, ErrorData, FPSDropData, LevelUpdatedData, MediaAttachedData, ManifestParsedData, FragLoadedData, HlsConfig} from 'hls.js';
import {IBitrateStats} from '../../video-player.interface';

export class VideoHlsBasePlayer extends VideoLiveStreamBasePlayer {
    protected _hlsInstance: Hls;
    protected _isManifestParsed: boolean = false;
    private _bitrateStats: IBitrateStats = {
        min: Number.MAX_VALUE,
        max: Number.MIN_VALUE
    }

    protected readonly HLS_CONFIG: HlsConfig = {
        ...Hls.DefaultConfig,
        capLevelOnFPSDrop: true
    }

    public static isSupported(): boolean {
        return Hls.isSupported();
    }

    constructor(protected readonly services: IServiceFactoryExtended, gameType: number, videoReference: HTMLVideoElement, streamSource: string, watchTimeLimit: number) {
        super('HLS', gameType, videoReference, streamSource, watchTimeLimit, services);
    }

    onWatchTimeLimitReached() {
        super.onWatchTimeLimitReached();
        this._hlsInstance.stopLoad();
        this._log('info', 'VideoPlayedBitrate', this._bitrateStats);
    }

    destroy() {
        this._hlsInstance?.off(Hls.Events.ERROR, this._onHlsError);
        this._hlsInstance?.off(Hls.Events.MEDIA_ATTACHED, this._onMediaAttached);
        this._hlsInstance?.off(Hls.Events.MANIFEST_PARSED, this._onManifestParsed);
        this._hlsInstance?.off(Hls.Events.LEVEL_UPDATED, this._onLevelUpdated);
        this._hlsInstance?.off(Hls.Events.FPS_DROP, this._onFpsDrop);
        this._hlsInstance?.off(Hls.Events.FRAG_LOADED, this._onBandwidthEstimate)

        this._hlsInstance?.destroy();
        super.destroy();

    }

    protected _onHlsError = (event: Events.ERROR, data: ErrorData) => {
        const {fatal} = data;
        if (fatal) {
            this._hlsInstance.destroy();
            this.handleError(data);
        }
    }

    protected _onMediaAttached = (event: Events.MEDIA_ATTACHED, data: MediaAttachedData) => {
        this._hlsInstance.loadSource(this.streamSource);
    }

    protected _onManifestParsed = (event: Events.MANIFEST_PARSED, data: ManifestParsedData) => {
        super.play();
        this._isManifestParsed = true;
    }

    protected _onLevelUpdated = (event: Events.LEVEL_UPDATED, data: LevelUpdatedData) => {
        const updatedBitrate = this._hlsInstance.levels[data.level].bitrate;
        if (updatedBitrate < this._bitrateStats.min) {
            this._bitrateStats.min = updatedBitrate;
        }
        if (updatedBitrate > this._bitrateStats.max) {
            this._bitrateStats.max = updatedBitrate;
        }
    }

    protected _onFpsDrop = (event: Events.FPS_DROP, data: FPSDropData) => {
        // fps drop event is triggered on every 'fpsDroppedMonitoringPeriod' interval, only if
        // 'capLevelOnFPSDrop' is set to true. It contains information regarding dropped frames
        // and total frames from the last interval
        // 'capLevelOnFPSDrop' default value is false and 'fpsDroppedMonitoringPeriod' is 5000 ms
        // more regarding configuration https://github.com/video-dev/hls.js/blob/master/docs/API.md
        // fps controller implementation https://github.com/video-dev/hls.js/blob/ca644667584c8128a7620c0df4ef9152c23451e9/src/controller/fps-controller.ts

        this._checkDroppedFPS(data.currentDropped, data.currentDecoded);
    }

    protected _onBandwidthEstimate = (event: Events.FRAG_LOADED, data: FragLoadedData) => {
        this.services.videoOnDemandPlaying.setBandwidthEstimate(this._hlsInstance.bandwidthEstimate);
    }
}
